import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useTranslation } from "react-i18next";
import StepRowCard from "../components/stepRowCard";
import StepContainer from "../stepContainer";
import AddButton from "../components/addButton";
import { getUserSkills } from "../../../services/profileService";
import StepDrawer from "../stepDrawer";
import { GRID_NAMES } from "../../../constants/gridNames";
import { API_PARAM } from "../../../constants/apiParams";
import DetailsDialogs from "../components/detailsDialogs";
import { saveStep } from "../../../services/profileService";
import { calculateAdvancedQuery } from "../utils/calculateAdvancedQuery";

const SkillSelection = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [skills, setSkills] = useState([]);
  const [initialSkills, setInitialSkills] = useState([]);
  const [drawerOpen, setdrawerOpen] = useState(false);
  const [dialog, setDialog] = useState({ open: false });
  const [gridInitialFilter, setGridInitialFilter] = useState(null);

  // On step load
  useEffect(() => {
    props.setProgressValue(25);
    getUserSkills().then((res) => {
      // Sort the skills by name
      setSkills(res.data.sort((a, b) => a.name.localeCompare(b.name)));
      // Save the initial skills to compare with the updated skills
      setInitialSkills(res.data);
    });
  }, []);

  useEffect(() => {
    let filterModel = calculateAdvancedQuery(skills);
    setGridInitialFilter(filterModel);
  }, [skills.length]);

  const onStepClose = async (goStepBack = false, stepCompleted = true) => {
    // All the skills that are deleted, updated or added
    let updatedSkills = [];
    // Check for skills that user deleted
    initialSkills.forEach((skill) => {
      if (!skills.find((x) => x.id === skill.id)) {
        updatedSkills.push({
          id: skill.selectedSkillId,
          isDeleted: true,
        });
      }
    });
    // Check for skills that user updated the proficiency Level
    skills.forEach((skill) => {
      const initSkill = initialSkills.find((x) => x.id === skill.id);
      if (initSkill && initSkill.proficiencyLevel !== skill.proficiencyLevel) {
        updatedSkills.push({
          id: skill.selectedSkillId,
          proficiencyLevel: skill.proficiencyLevel,
        });
      }
    });
    // Check for new skills that user added
    skills.forEach((skill) => {
      if (!initialSkills.find((x) => x.id === skill.id)) {
        updatedSkills.push({
          skillId: skill.id,
          proficiencyLevel: skill.proficiencyLevel,
          isNew: true,
        });
      }
    });
    let data = {
      currentStep: 1,
      isStepCompleted: stepCompleted,
      updatedSkills: updatedSkills,
    };
    // Request to save the user's Skills and then navigate to next step or previous step
    await saveStep(data);
    if (stepCompleted) {
      if (goStepBack) props.navigateStepBack();
      else props.navigateStepFront();
    }
  };

  useImperativeHandle(ref, () => ({
    onStepClose,
  }));

  // Delete skill from the list
  const handleDeleteSkill = (skillId) => {
    setSkills(skills.filter((skill) => skill.id !== skillId));
  };

  // Update skill proficiency level
  const setSkillLevel = (skillid, level) => {
    const updatedSkills = skills.map((skill) => {
      if (skill.id === skillid) {
        return { ...skill, proficiencyLevel: level };
      }
      return skill;
    });
    setSkills(updatedSkills);
  };

  // Add new skills to the list
  const addNewSkills = (newSkills) => {
    const skillsToAdd = [];
    newSkills.forEach((newSkill) => {
      const existingSkill = skills.find((skill) => skill.id === newSkill.id);
      if (!existingSkill) {
        const skillToAdd = {
          ...newSkill,
          proficiencyLevel: 1,
        };
        skillsToAdd.push(skillToAdd);
      }
    });
    setSkills((prevSkills) => [...skillsToAdd, ...prevSkills]);
  };

  // Grid column definitions for the skills selection drawer
  const columnDefs = [
    {
      cellRenderer: "CustomRadioCell",
      maxWidth: 45,
    },
    {
      field: "name",
      filter: true,
      cellRenderer: "CustomNameCell",
      autoHeight: true,
      maxWidth: 550,
    },
    {
      field: "category",
      hide: true,
      selectedFilterColumn: true,
      rowGroup: true,
    },
  ];

  return (
    <>
      {dialog?.open && <DetailsDialogs dialog={dialog} setDialog={setDialog} />}
      <StepDrawer
        open={drawerOpen}
        header={t("profileScreen.drawer.addSkillsHeader")}
        searchLabel={t("profileScreen.drawer.searchSkillsLabel")}
        filterLabel={t("profileScreen.drawer.searchSkillsFilterLabel")}
        onClose={() => setdrawerOpen(false)}
        onSave={addNewSkills}
        isSkillDrawer
        initialGridState={
          gridInitialFilter
            ? { filter: { filterModel: gridInitialFilter } }
            : null
        }
        columnDefs={columnDefs}
        gridName={GRID_NAMES.SKILL_SELECTION}
        datasourceUrl={API_PARAM.AVAILABLE_SKILLS}
        filterValuesUrl={API_PARAM.AVAILABLE_SKILL_VALUES}
      />
      <StepContainer
        stepTitle={t("profileScreen.skillSelectionStep.title")}
        stepDescription={t("profileScreen.skillSelectionStep.description")}
        onTransparentButtonClick={() => onStepClose(true)}
        transparentButtonLabel={t("profileScreen.btnBack")}
        onSecondaryButtonClick={onStepClose}
        secondaryButtonLabel={t("profileScreen.btnNext")}
        hasLargeHeader
        header={
          <AddButton
            label={t("profileScreen.skillSelectionStep.btnAddSkills")}
            onClick={() => setdrawerOpen(true)}
          />
        }
      >
        {skills &&
          skills.map((skill) => (
            <StepRowCard
              key={skill.id}
              title={skill.name}
              description={skill.category}
              hasHover
              hasDelete
              onDelete={() => handleDeleteSkill(skill.id)}
              hasLevels={props.skillProficiencyLevelsEnabled}
              level={skill.proficiencyLevel}
              setLevel={(level) => setSkillLevel(skill.id, level)}
              onInfoClicked={() => setDialog({ open: true, skillId: skill.id, proficiencyLevel: skill.proficiencyLevel })}
            />
          ))}
      </StepContainer>
    </>
  );
});

export default SkillSelection;
